import React from 'react';
import { Drawer, Form, Input, InputNumber, DatePicker, Select, Button, Row, Col, Space, message } from 'antd';
import dayjs from 'dayjs';
import { insert, update } from '../../../../apis/DiasFestivosApi';
import { getAll } from '../../../../apis/OdooScheduleApi';

// --- Mocks: DEJAR sólo para catálogo de horarios ---
const mockFetchSchedules = () =>
  new Promise((resolve) =>
    setTimeout(
      () =>
        resolve([
          { value: 'ALL_DAY', label: 'Todo el día' },
          { value: '08_18', label: '08:00 – 18:00' },
          { value: '09_14', label: '09:00 – 14:00' },
        ]),
      400
    )
  );

const toDayjs = (d) => (d ? dayjs(d) : null);
const toStr = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
const pad = (n) => String(n).padStart(2, '0');

const AddHoliday = ({ open, holiday, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [schedules, setSchedules] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Modo de captura UI: 'single' (día/mes/año) | 'range' (fechas) | null
  const [mode, setMode] = React.useState(null);

  // Cargar catálogo mock cuando abra
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (open && schedules.length === 0) {
        const data = await getAll();
        console.log('Fetched schedules:', data);
        if (mounted) setSchedules(data.payload || []);
      }
    })();
    return () => { mounted = false; };
  }, [open, schedules.length]);

  // Poblar formulario al abrir (crear o editar)
  React.useEffect(() => {
    if (open) {
      // set valores
      const h = holiday || {};
      const df = h?.date_from;
      const dt = h?.date_to;

      form.setFieldsValue({
        id: h?.id,
        nombre: h?.name || h?.display_name || '',
        display_name: h?.display_name || '',
        // captura "single"
        dia: h?.day ?? (df ? dayjs(df).date() : undefined),
        mes: h?.month ?? (df ? dayjs(df).month() + 1 : undefined),
        anio: df ? dayjs(df).year() : undefined,
        // captura "range"
        fecha_inicio: toDayjs(df),
        fecha_fin: toDayjs(dt),
        // mock horario
        horario: undefined,
        // ids crudos (si necesitas pasarlos)
        calendar_id: h?.calendar_id ?? h?.calendar?.id ?? null,
        odoo_calendar_id: h?.odoo_calendar_id ?? null,
      });

      // Establecer modo inicial: si hay rango y df!=dt => range; si df==dt => single; sino null
      if (df && dt && df !== dt) setMode('range');
      else if (df && dt && df === dt) setMode('single');
      else setMode(null);
    } else {
      form.resetFields();
      setMode(null);
    }
  }, [open, holiday, form]);

  const handleSingleInteract = () => { if (mode !== 'single') setMode('single'); };
  const handleRangeInteract = () => { if (mode !== 'range') setMode('range'); };

  const onSubmit = async (values) => {
    const isEdit = !!values.id;

    // Construcción base del payload para backend (DiasFestivosBase)
    const basePayload = {
      id: values.id ?? undefined,
      name: values.nombre,
      display_name: values.display_name || values.nombre,
      calendar_id: values.calendar_id ?? null,      // aún sin catálogo real
      odoo_calendar_id: values.odoo_calendar_id ?? null,
    };

    try {
      setLoading(true);

      if (mode === 'single') {
        const { dia, mes, anio } = values;
        if (dia == null || mes == null || anio == null) {
          message.error('Completa Día, Mes y Año.');
          setLoading(false);
          return;
        }
        const dateStr = `${anio}-${pad(mes)}-${pad(dia)}`;
        const payload = {
          ...basePayload,
          date_from: dateStr,
          date_to: dateStr,
          month: mes,
          day: dia,
        };

        if (isEdit) await update(payload);
        else await insert(payload);

        message.success(isEdit ? 'Festivo actualizado' : 'Día festivo creado');
        onSaved?.();
        onClose();
        return;
      }

      if (mode === 'range') {
        const { fecha_inicio, fecha_fin } = values;
        if (!fecha_inicio || !fecha_fin) {
          message.error('Selecciona Fecha Inicio y Fecha Fin.');
          setLoading(false);
          return;
        }
        const fromStr = toStr(fecha_inicio);
        const toStrVal = toStr(fecha_fin);
        const inferredMonth = dayjs(fecha_inicio).month() + 1;
        const inferredDay = dayjs(fecha_inicio).date();

        const payload = {
          ...basePayload,
          date_from: fromStr,
          date_to: toStrVal,
          month: values.mes ?? inferredMonth,
          day: values.dia ?? inferredDay,
        };

        if (isEdit) await update(payload);
        else await insert(payload);

        message.success(isEdit ? 'Festivo actualizado' : 'Periodo festivo creado');
        onSaved?.();
        onClose();
        return;
      }

      message.error('Indica si capturarás por Día/Mes/Año o por Rango de Fechas.');
    } catch (e) {
      message.error(isEdit ? 'No se pudo actualizar el festivo' : 'No se pudo crear el festivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={holiday ? 'Editar Día Festivo' : 'Agregar Día Festivo'}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Guardar
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item name="id" hidden><Input /></Form.Item>
        <Form.Item name="calendar_id" hidden><InputNumber style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="odoo_calendar_id" hidden><InputNumber style={{ width: '100%' }} /></Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button onClick={() => {
            form.resetFields(['dia','mes','anio','fecha_inicio','fecha_fin','horario']);
            setMode(null);
          }} type="link">
            Limpiar selección
          </Button>
        </div>

        <Form.Item
          label="Nombre del día festivo"
          name="nombre"
          rules={[
            { required: true, message: 'Captura el nombre del festivo' },
            { max: 220, message: 'Máximo 220 caracteres' },
            { whitespace: true, message: 'El nombre no puede ser solo espacios' },
          ]}
        >
          <Input placeholder="Ej. Navidad, Año Nuevo, etc." maxLength={220} allowClear />
        </Form.Item>

        <Form.Item
          label="Descripción (display_name)"
          name="display_name"
          rules={[{ max: 220, message: 'Máximo 220 caracteres' }]}
        >
          <Input placeholder="Texto visible" maxLength={220} allowClear />
        </Form.Item>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Día" name="dia">
              <InputNumber
                min={1}
                max={31}
                style={{ width: '100%' }}
                disabled={mode === 'range'}
                onFocus={handleSingleInteract}
                onChange={handleSingleInteract}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Mes" name="mes">
              <InputNumber
                min={1}
                max={12}
                style={{ width: '100%' }}
                disabled={mode === 'range'}
                onFocus={handleSingleInteract}
                onChange={handleSingleInteract}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Año" name="anio">
              <InputNumber
                min={1900}
                max={3000}
                style={{ width: '100%' }}
                disabled={mode === 'range'}
                onFocus={handleSingleInteract}
                onChange={handleSingleInteract}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Fecha Inicio" name="fecha_inicio">
              <DatePicker
                style={{ width: '100%' }}
                disabled={mode === 'single'}
                onFocus={handleRangeInteract}
                onChange={handleRangeInteract}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Fecha Fin" name="fecha_fin">
              <DatePicker
                style={{ width: '100%' }}
                disabled={mode === 'single'}
                onFocus={handleRangeInteract}
                onChange={handleRangeInteract}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Schedules mock (visual por ahora) */}
        <Form.Item
          label="Horario"
          name="horario"
        >
          <Select
            placeholder="Selecciona un horario"
            options={schedules.map(s => ({
              label: s.descripcion,
              value: Number(s.id),
            }))}
            loading={schedules.length === 0}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddHoliday;